import { useCallback, useEffect, useRef, useState } from "react";
import { Bell, Lock, LogOut, Moon, Settings, User, Users } from "react-feather";
import { BiFilter } from "react-icons/bi";
import { FaMobileScreen } from "react-icons/fa6";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import admin from "../assets/user.png";
import useProfilePicture from "../hooks/useProfilePicture";
import { clearAuthDetails } from "../auth/authHelper";
import { authServerUrl, consoleUrl, FORMS, MODULES } from "../const";
import useUserCompany from "../hooks/useUserCompany";
import { customHistory } from "../router/CustomBrowserRouter";
import { userActions } from "../store/user/user-slice";
import { useNotifications } from "./notification/NotificationProvider";

function UserDropDown({
  setShowUserDropdown,
  handleOpenRegisterDevices, // existing
  openUserAccount, // NEW: Header callback to open UserAccount modal
  openGlobalCustomer, // NEW: Header callback to open GlobalCustomer modal
  anyModalOpen, // NEW: Header tells us if any modal is open
}) {
  const userDetail = useSelector((s) => s.user.userDetails);
  const [darkModeEnabled, setDarkModeEnabled] = useState(false);
  const { toggleNotifications, enabled } = useNotifications();
  const dispatch = useDispatch();

  const profilePicUrl = useProfilePicture(userDetail?.uploadDocumentSeq);

  // permission
  const auth = useSelector((state) => state.user.permissions[MODULES?.Master]);
  const customerFilterPermission = auth?.[FORMS?.CUSTOMER_FILTER?.FILTER?.form];
  const viewCustomerFilter = customerFilterPermission?.FORM?.VIEW;
  const notificationTogglePermission = auth?.NOTIFICATION_TOGGLE;
  const viewNotificationToggle = notificationTogglePermission?.FORM?.VIEW;

  // logout plumbing
  const popupRef = useRef(null);
  const timerRef = useRef(null);
  const doLocalLogout = useCallback(() => {
    dispatch(userActions.clearAuthData(true));
    clearAuthDetails(true);
    customHistory.push("/login");
  }, [dispatch]);

  const handleLogout = useCallback(() => {
    popupRef.current = window.open(
      `${authServerUrl}/logout`,
      "logoutPopup",
      "width=500,height=600",
    );
    timerRef.current = window.setInterval(() => {
      if (!popupRef.current || popupRef.current.closed) {
        clearInterval(timerRef.current);
        doLocalLogout();
      }
    }, 200);
  }, [doLocalLogout]);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      popupRef.current?.close();
    };
  }, []);

  // close the dropdown when clicking outside — BUT NOT while a modal is open
  const dropdownRef = useRef(null);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (anyModalOpen) return; // ✅ don't auto-close dropdown while a modal is up
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        if (!event.target.closest(".user-drop-container")) {
          setShowUserDropdown(false);
        }
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [anyModalOpen, setShowUserDropdown]);

  const handleUserConsoleClick = () => {
    const consoleUrlWithParams = `${consoleUrl}/console/login?preAuthorized=true`;
    window.open(consoleUrlWithParams, "_blank", "noopener noreferrer");
  };

  const handleChangePasswordClick = () => {
    const url = `${consoleUrl}/console/login?preAuthorized=true&action=changePassword`;
    window.open(url, "_blank", "noopener noreferrer");
  };

  return (
    <div
      className="user-drop-container"
      ref={dropdownRef}
      style={{ zIndex: 10001, position: "absolute" }}
    >
      <div className="user-header">
        <img
          src={profilePicUrl || admin}
          alt="admin"
          width="40px"
          height="40px"
          style={{ borderRadius: "50%", objectFit: "cover" }}
          onError={(e) => {
            e.target.src = admin;
          }}
        />
        <div className="user-name">
          <h4>
            {userDetail?.firstName
              ? userDetail?.firstName
              : "" + (userDetail?.lastName ? userDetail?.lastName : "") ||
                "Administrator"}
          </h4>
          <h5>{userDetail?.email || "sample.email@gmail.com"}</h5>
        </div>
      </div>
      <div className="ui clearing divider"></div>

      <div className="user-menu-item" onClick={handleOpenRegisterDevices}>
        <FaMobileScreen size={20} />
        <h5>Mobile Device Manager</h5>
      </div>

      <div className="ui clearing divider"></div>

      <div className="user-menu-item">
        <Settings size={20} />
        <h5>Settings</h5>
      </div>

      <div className="user-menu-item" onClick={handleUserConsoleClick}>
        <Users size={20} />
        <h5>User Console</h5>
      </div>

      <div className="user-menu-item" onClick={handleChangePasswordClick}>
        <Lock size={20} />
        <h5>Change Password</h5>
      </div>

      <div
        onClick={() => {
          setShowUserDropdown(false); // close the dropdown
          if (openUserAccount) openUserAccount(); // open modal in Header
        }}
        className="user-menu-item"
      >
        <User size={20} />
        <h5>Account</h5>
      </div>

      {viewCustomerFilter && (
        <div
          onClick={() => {
            setShowUserDropdown(false); // close the dropdown
            openGlobalCustomer(); // open modal in Header
          }}
          className="user-menu-item"
        >
          <BiFilter size={20} />
          <h5>Customer Filtration</h5>
        </div>
      )}

      <div className="ui clearing divider"></div>

      {viewNotificationToggle && (
        <div className="user-menu-item">
          <Bell size={20} />
          <h5>Notification</h5>
          <div
            className="ui right aligned toggle checkbox toggle-switch"
            style={{ marginRight: "0" }}
          >
            <input
              type="checkbox"
              id="notificationToggle"
              checked={enabled}
              onChange={() => toggleNotifications(!enabled)}
            />
            <label htmlFor="notificationToggle"></label>
          </div>
        </div>
      )}

      <div className="user-menu-item">
        <Moon size={20} />
        <h5>Dark Mode</h5>
        <div
          className="ui right aligned toggle checkbox toggle-switch"
          style={{ marginRight: "0" }}
        >
          <input
            type="checkbox"
            id="darkModeToggle"
            checked={darkModeEnabled}
            onChange={() => setDarkModeEnabled((v) => !v)}
          />
          <label htmlFor="darkModeToggle"></label>
        </div>
      </div>

      <div className="ui clearing divider"></div>

        <div className="user-menu-item" onClick={handleLogout}>
          <LogOut size={20} />
          <Link>
            <h5 style={{ color: "#C72127" }}>Log out</h5>
          </Link>
        </div>
      </div>
  );
}

export default UserDropDown;
